import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useSnackBar from "../../../../components/Commons/SnackBar/useSnackBar";
import { useEffect, useState } from "react";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Spinner } from "../../../../components/Commons/Spinner/Spinner";
import { SalesInvoice } from "../../../../interfaces/Sales/Invoices/salesinvoices";
import { Box, Button } from "@mui/material";
import { Header } from "../../../../components/Header";
import { ButtonStyled, PrimaryButton } from "../../../../theme/buttons";
import EmailIcon from '@mui/icons-material/Email';
import { useCompanyStore } from "../../../../store/AuthStore";
import { DialogSendEmails } from "../helpers/dialogSendEmails";
import { useGridPreferences } from "../../../../hooks/useGridPreferences";
import { useCurrencies } from "../../../../hooks/useCurrencies";
import { ErrorDisplay } from "../../../../components/Commons/Error/ErrorDisplay";
import { AxiosError } from "axios";
import { useParamsFilter } from "../../../../hooks/useParamsFilter";
import { useFormatNumber } from "../../../../hooks/useFormatNumber";
import { useSalesInvoiceColumns } from "./columns";
import { usePreferenceTypesStore } from "../../../../store/Preferences/PreferencesStore";
import { PreferenceType } from "../../../../interfaces/Commons/Preferences/preferences";
import DataTableSticky from "../../../../components/Tables/GridTableMaterialUI/DataTableSticky";
import { FilterTable } from "../../../../components/Filter";

export const TableSalesInvoices = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const { LoadExchangeRateHistory } = useCurrencies();
  const { companyState } = useCompanyStore();
  const { ConvertParamsToBase64 } = useParamsFilter();
  const { setFormatNumberFromTable } = useFormatNumber();
  const { columnsSalesInvoices } = useSalesInvoiceColumns();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openDialogEmail, setOpenDialogEmail] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const [myPreferences, setMyPreferences] = useState({});
  const [mySortPreference, setMySortPreference] = useState([]);
  const [salesInvoicesData, setSalesInvoicesData] = useState<GridRowsProp>([]);
  const [dataSalesInvoiceSelected, setDataSalesInvoiceSelected] = useState<GridRowsProp>([]);
  const [dataEmails, setDataEmails] = useState<any>([]);
  const [columns, setColumns] = useState<GridColDef<SalesInvoice>[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const initFilter = '/salesinvoices?filter='
  const defaultFilter = [{ idFilter: 1, column: "invoice_doc_status_name", operator: "=", value: "Activa" }]
  const [isLoadingData, setIsLoadingData] = useState(true);

  const onSelectedRows = (rowsIds: any) => {
    let selectedSalesInvoices = rowsIds.map((element: number) => salesInvoicesData?.find((elementFind) => elementFind["purchase_invoice_id"] === element));
    setDataSalesInvoiceSelected(selectedSalesInvoices);
  };

  const onSendEmail = () => {
    let dataEmails: any = [];
    setDataEmails(dataSalesInvoiceSelected.forEach((element: any) => {
      let data = {
        data: element,
        status: (element.supplier_email) ? "Waiting" : "Error",
        status_description:
          (element.supplier_email) ? "generic.send_email.waiting" :
            "generic.send_email.no_email"
      }
      dataEmails.push(data)
    }));
    setDataEmails(dataEmails);
    setOpenDialogEmail(true);
  }

  const myCustomColumns: GridColDef<any>[] = [
    {
      field: 'project_name', headerName: t("projects.fields.project_name"), headerClassName: 'header-grid-table', flex: 1,
      renderCell(params: any) {
        return (
          <Button onClick={() => {
            const stringConverted = ConvertParamsToBase64(`project_id=${params.row.project_id}`);
            window.open(`/projects/view?${stringConverted}`, '_blank');
          }}>
            {params.row.project_name}
          </Button>
        );
      }
    },
    {
      field: 'total_amount', headerName: t("salesinvoices.fields.total_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'subtotal_amount', headerName: t("salesinvoices.fields.subtotal_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'tax_amount', headerName: t("salesinvoices.fields.tax_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'exchange_rate', headerName: t("salesinvoices.fields.exchange_rate"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'discount_amount', headerName: t("salesinvoices.fields.discount_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'retention_amount', headerName: t("salesinvoices.fields.retention_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
  ];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/salesinvoices",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            setColumns(await LoadColumnsOrder("/salesinvoices", myCustomColumns, preference?.preference_value));
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/salesinvoices",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/salesinvoices", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/salesinvoices",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/salesinvoices", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsSalesInvoices);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await LoadExchangeRateHistory()
          .catch((error: unknown) => {
            if (error instanceof AxiosError) {
              if (error.response && error.response.status === 400) {
                showSnackBar(error.response.data, "error");
              } else {
                showSnackBar(error.message, "error");
              }
            } else {
              showSnackBar("An unexpected error occurred", "error");
            }
          });
        await loadTablePreferences();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };
    document.title = `${companyState?.comercial_name} - ${t("salesinvoices.title")}`;
    if (location.state !== null) {
      if (location.state.row && location.state.row.purchase_invoice_id) {
        navigate("/salesinvoices/view", { state: { row: location.state.row } });
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (isLoadedMyLocalPreferences && salesInvoicesData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [salesInvoicesData, isLoadedMyLocalPreferences]);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && !hasError &&
        <>
          <Header
            size="sm"
            title={t("salesinvoices.title")}
            child={
              <Box display={"flex"} flexDirection={"row-reverse"}>
                <PrimaryButton
                  variant='outlined'
                  onClick={() => { navigate("add", { state: { ...location.state } }) }}
                  size="small"
                >
                  {t("generic.buttons.add")}
                </PrimaryButton>
                <ButtonStyled
                  variant='outlined'
                  size="small"
                  className="mx-1"
                  sx={{ color: 'primary.main' }}
                  disabled={dataSalesInvoiceSelected.length === 0}
                  onClick={onSendEmail}
                >{t("generic.buttons.send_email")}
                  <EmailIcon sx={{ marginLeft: 1 }} />
                </ButtonStyled>
                <FilterTable
                  setIsLoadingDataTable={setIsLoadingData}
                  setDataTable={setSalesInvoicesData}
                  showSnackBar={showSnackBar}
                  columnsTable={columnsSalesInvoices}
                  initFilter={initFilter}
                  resourceRoute="/salesinvoices"
                  defaultFilter={defaultFilter}
                />
              </Box>
            }
          />
          {isLoadingDataTable &&
            <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
              <DataTableSticky
                columns={columns}
                setColumns={setColumns}
                data={salesInvoicesData ?? []}
                entityId={"sales_invoice_id"}
                entity={`SalesInvoices`}
                preferences={myPreferences}
                namePreferences={"/salesinvoices"}
                nameOrderColumns={"/salesinvoices"}
                selectRows={true}
                countColumns={[
                  "total_company_currency_amount",
                  "total_amount",
                  "subtotal_amount",
                  "tax_amount",
                  "discount_amount",
                  "retention_amount",
                ]}
                onRowSelectionModelChange={(rowsIds) => onSelectedRows(rowsIds)}
                sortPreference={mySortPreference}
                isLoading={isLoadingData}
              />
            </Box>
          }
          <DialogSendEmails
            open={openDialogEmail}
            setOpen={setOpenDialogEmail}
            data={dataEmails}
            showSnackBar={showSnackBar}
          />
        </>
      }
      <SnackbarComponent />
    </>
  )
}
